import { Injectable } from '@angular/core';
import { CtAttributesService } from '@cloud-twin-v2/cloud-twin-shared/core/attributes/ct-attributes.service';
import { CTResultsService } from '@cloud-twin-v2/cloud-twin-shared/core/results/ct-results.service';
import { AttributeInfoDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { ArrayHelperService } from '@common-modules/shared/helpers/array-helper.service';
import { DateHelperService } from '@common-modules/shared/helpers/date-helper.service';
import { ExtendDecimalPipe } from '@common-modules/shared/localization/extend-decimal.pipe';
import { DynamicRenderizerComponentService } from '@common-modules/shared/services/dynamic-renderizer-component.service';
import { LayerItemV2 } from '@common-modules/wlm-map/generic-map/models/layer-item';
import { NativeLayer } from '@common-modules/wlm-map/generic-map/models/native-layer';
import { BaseLayerItemService } from '@common-modules/wlm-map/generic-map/services/base-layer-item.service';
import { GenericMapStylesService } from '@common-modules/wlm-map/generic-map/services/generic-map-styles.service';
import { MapDateRange } from '@common-modules/wlm-map/models/map-date-range';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator,
} from '@progress/kendo-data-query';
import { Observable, ReplaySubject, map, take } from 'rxjs';
import {
  CtMapTooltipComponent,
  CtMapTooltipParams,
} from '../ct-map-tooltip/ct-map-tooltip.component';
import { AttributeResultByDate } from '../models/attribute-result-by-date';
import { CTMapFiltrableProperties } from '../models/ct-map-filtrable-properties';
import { CTLayerItemType } from '../models/ct-map.constants';
import { CtResult } from '../models/ct-result';
import { CTMapPersistencyService } from './ct-map-persistency.service';

@Injectable()
export class CTLayerItemService extends BaseLayerItemService {
  type = CTLayerItemType;

  constructor(
    private readonly _arrayHelperService: ArrayHelperService,
    readonly _mapStylesService: GenericMapStylesService,
    private readonly _resultsService: CTResultsService,
    private readonly _dynamicRenderizerService: DynamicRenderizerComponentService,
    private readonly _dateHelperService: DateHelperService,
    private readonly _ctAttributesService: CtAttributesService,
    private readonly _decimalPipe: ExtendDecimalPipe,
    private readonly _ctMapPersistencyService: CTMapPersistencyService,
  ) {
    super(_mapStylesService);
  }

  getFilters(properties: CTMapFiltrableProperties): CompositeFilterDescriptor {
    const scenarioIds = this.getScenarioIds(properties);

    const scenarioFilter: CompositeFilterDescriptor = {
      logic: 'or',
      filters: scenarioIds.map(
        (scenarioId) =>
          ({
            field: 'scenarioId',
            operator: FilterOperator.EqualTo,
            value: scenarioId,
          }) as FilterDescriptor,
      ),
    };

    const neTypeFilter: FilterDescriptor = {
      field: 'neType',
      operator: FilterOperator.EqualTo,
      value: properties.neType,
    };

    const allFilters: CompositeFilterDescriptor = {
      logic: 'and',
      filters: [neTypeFilter, scenarioFilter],
    };

    return allFilters;
  }

  getResults(properties: CTMapFiltrableProperties, timeStep: Date): Observable<CtResult[]> {
    return this._resultsService.results$.pipe(take(1));
  }

  getInnerLayerAttributes(
    innerLayer: NativeLayer,
    attributesGroups: CtResult[],
    timeStep: Date,
  ): Map<string, any> {
    const timeStepFormatted = this._dateHelperService.toApiExactISO(timeStep);
    const properties: { neId: string; scenarioId: string } = innerLayer.feature.properties as any;

    const group = attributesGroups.find((group) => group.scenarioId === properties.scenarioId);
    const neAttributes = group?.networkElements.find(
      (item) => item.networkElementId === properties.neId,
    );

    const attributesMap = new Map<string, any>();
    if (neAttributes) {
      neAttributes.attributes.forEach((attribute: AttributeResultByDate) => {
        attributesMap.set(attribute.name, attribute.values[timeStepFormatted]);
      });
    }

    return attributesMap;
  }

  getTooltip(tooltipParams: CtMapTooltipParams): Observable<HTMLElement> {
    const isLoaded$ = new ReplaySubject<void>(1);

    const tooltipComponent = this._dynamicRenderizerService.injectComponentWithoutDestroyIt(
      CtMapTooltipComponent,
      'div',
      (component: CtMapTooltipComponent) => {
        component.params = tooltipParams;
        //component.allLayerItems$ = allLayerItems;

        setTimeout(() => {
          isLoaded$.next();
          isLoaded$.complete();
        });
      },
    );

    return isLoaded$.asObservable().pipe(map(() => tooltipComponent));
  }

  getDateRange(properties: CTMapFiltrableProperties): Observable<MapDateRange> {
    const scenarioIds = this.getScenarioIds(properties);
    return this._ctAttributesService.getDateRange(scenarioIds).pipe(
      map((dateRange) => {
        return dateRange;
      }),
    );
  }

  formatAttributes(
    attributes: Map<string, any>,
    nativeLayer: NativeLayer,
  ): Observable<Map<string, string>> {
    // The metadata load is triggered when the workspace is selected.
    return this._ctAttributesService.metadataByAttributeName$.pipe(
      take(1),
      map((metadataByAttributeName) => {
        const formattedAttributes = new Map<string, string>();
        const scenarioId = nativeLayer.feature.properties?.scenarioId;

        for (const [attributeName, value] of attributes.entries()) {
          const metadata = metadataByAttributeName.get(scenarioId)?.get(attributeName);
          let formattedValue = value ?? '--';

          if (
            metadata?.format &&
            metadata.attributeTypeKey == 'Double' &&
            value != '--' &&
            value != ''
          ) {
            const format = metadata?.format ?? '1.0-0';
            formattedValue = this._decimalPipe.transform(value, format) ?? formattedValue;
          }

          if (metadata?.units) {
            formattedValue = `${formattedValue} ${metadata.units}`;
          }

          formattedAttributes.set(attributeName, formattedValue);
        }

        return formattedAttributes;
      }),
    );
  }

  getElementAdditionalProperties(
    innerLayer: NativeLayer,
    attributesGroups: CtResult[],
  ): Map<string, any> {
    const properties: { neId: string; scenarioId: string } = innerLayer.feature.properties as any;

    const group = attributesGroups.find((group) => group.scenarioId === properties.scenarioId);
    const ne = group?.networkElements.find((item) => item.networkElementId === properties.neId);

    const neProperties = new Map<string, any>();

    if (ne) {
      neProperties.set('networkElementName', ne.networkElementName);
    }

    return neProperties;
  }

  getLabelPropertiesMap(nativeLayer: NativeLayer): Observable<Map<string, any>> {
    return this._ctAttributesService.metadataByAttributeName$.pipe(
      take(1),
      map((metadataByAttributeName) => {
        const scenarioId = nativeLayer.feature.properties?.scenarioId;

        const metadata = metadataByAttributeName.get(scenarioId);

        metadata.set('neType', new AttributeInfoDto({ alias: 'Network Element Type ' }));
        metadata.set('networkElementName', new AttributeInfoDto({ alias: 'Network Element Name' }));

        return metadata;
      }),
    );
  }

  hasLayerId(nativeLayer: NativeLayer, layerId: string): boolean {
    return nativeLayer?.pane === layerId;
  }

  persistLayerVisibility(layerItem: LayerItemV2, isVisible: boolean): void {
    this._ctMapPersistencyService.persistLayerVisibility(layerItem, isVisible);
  }

  persistLayersOrder(layerItems: LayerItemV2[]): void {
    this._ctMapPersistencyService.persistLayersOrder(layerItems);
  }

  getInnerLayerId(nativeLayer: NativeLayer): string {
    return nativeLayer?.feature?.properties?.neId;
  }

  private getScenarioIds = (properties: CTMapFiltrableProperties) =>
    this._arrayHelperService.onlyUnique(
      properties.namespaces.map((namespace) => namespace.scenarioId),
    );
}
